import { Lead } from '../types/lead';

/**
 * Zugriff auf die meta_data eines Leads, unabhängig davon, aus welcher Quelle
 * der Lead stammt.
 *
 * Meta-Formulare und der Funnel (über n8n) schreiben dieselben Inhalte unter
 * unterschiedlichen Schlüsseln:
 *   Meta:   Zeitraum, Ort, Straße
 *   Funnel: zeitraum, ort, strasse, flaeche
 *
 * Deshalb werden Schlüssel vor dem Vergleich normalisiert: klein geschrieben,
 * Umlaute und ß aufgelöst. "Fläche", "flaeche" und "FLACHE" gelten damit als
 * derselbe Schlüssel.
 */

/** Vereinheitlicht einen Schlüssel für den Vergleich: klein, ohne Umlaute/ß. */
const normalizeKey = (key: string): string =>
  key
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');

/**
 * Liefert die meta_data als Objekt.
 *
 * n8n liefert das Feld je nach Node-Konfiguration auch als JSON-String; in dem
 * Fall wird es hier geparst, damit die Anzeige nicht leer bleibt.
 */
export const getMetaData = (lead: Lead): Record<string, any> => {
  const meta = lead?.meta_data as unknown;

  if (typeof meta === 'string') {
    try {
      const parsed = JSON.parse(meta);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  return meta && typeof meta === 'object' ? (meta as Record<string, any>) : {};
};

/**
 * Liest einen Meta-Feld-Wert. Mehrere Schlüssel-Varianten können übergeben
 * werden, der erste gefüllte Treffer gewinnt. Leere Strings gelten als nicht
 * vorhanden (der Funnel schickt z. B. "ad_name": "").
 */
export const metaValue = (lead: Lead, ...keys: string[]): string | undefined => {
  const entries = Object.entries(getMetaData(lead));

  for (const key of keys) {
    const gesucht = normalizeKey(key);
    const hit = entries.find(([k]) => normalizeKey(k) === gesucht);
    if (hit && hit[1] != null && String(hit[1]).trim()) return String(hit[1]).trim();
  }

  return undefined;
};
